import { useState, Component, ReactNode, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardBody, Button, Tabs, TabsList, TabsTrigger, TabsContent } from '@components/ui';
import { CurrentSubscriptionCard } from '@features/subscriptions/components/CurrentSubscriptionCard';
import { FiscalInfoTab } from '@features/certificates/components/FiscalInfoTab';
import { TemplatesTab } from '@features/templates/components/TemplatesTab';
import Swal from 'sweetalert2';

// Error Boundary for debugging
class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded">
          <h3 className="text-red-900 font-bold">Error en Templates:</h3>
          <p className="text-red-700">{this.state.error?.message}</p>
          <pre className="mt-2 text-xs overflow-auto">{this.state.error?.stack}</pre>
        </div>
      );
    }

    return this.props.children;
  }
}

export function AdminSettingsPage() {
  const [searchParams] = useSearchParams();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [invoicePrefix, setInvoicePrefix] = useState('FE');
  const [defaultTaxRate, setDefaultTaxRate] = useState('7');

  // Get the tab from URL query params, default to "general"
  const tabFromUrl = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  console.log('🔍 AdminSettingsPage - URL params:', {
    allParams: Object.fromEntries(searchParams.entries()),
    tabParam: searchParams.get('tab'),
    tabFromUrl,
    activeTab
  });

  // Update active tab when URL param changes
  useEffect(() => {
    const tab = searchParams.get('tab') || 'general';
    console.log('🔄 AdminSettingsPage - useEffect triggered, tab from URL:', tab);
    console.log('✅ AdminSettingsPage - Setting active tab to:', tab);
    setActiveTab(tab);
  }, [searchParams]);

  const handleSave = () => {
    Swal.fire({
      title: 'Éxito',
      text: 'Configuración guardada exitosamente',
      icon: 'success',
      confirmButtonText: 'Aceptar',
    });
  };

  console.log('🎨 AdminSettingsPage - Rendering with activeTab:', activeTab);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Configuración Administrativa
        </h1>
        <p className="mt-2 text-secondary-600 dark:text-secondary-400">
          Administra las configuraciones del sistema
        </p>
      </div>

      {/* Subscription Card */}
      <CurrentSubscriptionCard />

      <Tabs
        value={activeTab}
        onValueChange={(newTab) => {
          console.log('📍 Tabs - onValueChange called with:', newTab);
          setActiveTab(newTab);
        }}
        className="w-full"
      >
        <TabsList>
          <TabsTrigger value="general">Configuración General</TabsTrigger>
          <TabsTrigger value="fiscal">Información Fiscal y Certificados</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <div className="space-y-6">
            <Card>
              <CardBody>
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
                  Configuración de Facturación
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Prefijo de Facturas
                    </label>
                    <input
                      type="text"
                      value={invoicePrefix}
                      onChange={(e) => setInvoicePrefix(e.target.value)}
                      className="w-full max-w-xs rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 opacity-50 cursor-not-allowed"
                      placeholder="FE"
                      disabled
                    />
                    <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                      Prefijo para el número de factura
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Tasa de Impuesto por Defecto (%)
                    </label>
                    <input
                      type="number"
                      value={defaultTaxRate}
                      onChange={(e) => setDefaultTaxRate(e.target.value)}
                      className="w-full max-w-xs rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 opacity-50 cursor-not-allowed"
                      placeholder="7"
                      step="0.01"
                      disabled
                    />
                    <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                      Tasa de impuesto aplicada por defecto
                    </p>
                  </div>

                  <div className="flex items-center opacity-50">
                    <input
                      type="checkbox"
                      id="autoApprove"
                      checked={autoApprove}
                      onChange={(e) => setAutoApprove(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded cursor-not-allowed"
                      disabled
                    />
                    <label htmlFor="autoApprove" className="ml-3 cursor-not-allowed">
                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                        Auto-aprobar facturas
                      </span>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        Las facturas se aprobarán automáticamente sin revisión manual
                      </p>
                    </label>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
                  Notificaciones
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center opacity-50">
                    <input
                      type="checkbox"
                      id="emailNotifications"
                      checked={emailNotifications}
                      onChange={(e) => setEmailNotifications(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded cursor-not-allowed"
                      disabled
                    />
                    <label htmlFor="emailNotifications" className="ml-3 cursor-not-allowed">
                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                        Notificaciones por email
                      </span>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        Recibir notificaciones por correo electrónico
                      </p>
                    </label>
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
                  Seguridad
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center opacity-50">
                    <input
                      type="checkbox"
                      id="twoFactorAuth"
                      checked={twoFactorAuth}
                      onChange={(e) => setTwoFactorAuth(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded cursor-not-allowed"
                      disabled
                    />
                    <label htmlFor="twoFactorAuth" className="ml-3 cursor-not-allowed">
                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                        Autenticación de dos factores
                      </span>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">
                        Requerir código de verificación adicional al iniciar sesión
                      </p>
                    </label>
                  </div>
                </div>
              </CardBody>
            </Card>

            <div className="flex justify-end gap-3">
              <Button data-guide="Guardar Configuración" type="button" variant="primary" onClick={handleSave}>
                Guardar Configuración
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="fiscal">
          <FiscalInfoTab />
        </TabsContent>

        <TabsContent value="templates">
          <ErrorBoundary>
            <TemplatesTab />
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
}
